/**
 * admin.service.ts
 *
 * Typed API call wrappers for the admin configuration domain.
 * All methods use the shared apiClient (Axios instance with JWT interceptor).
 * Returns unwrapped data from the response envelope.
 */
import { apiClient } from './apiClient';
import { uploadFile } from './uploads.service';
import type { ApiResponse } from '@/types/api.types';
import type {
  FieldModeState,
  FormMode,
  OptionHintList,
  ResolvedField,
} from '@/pages/leads/newPatientForm/newPatientFormRules';

// ─── Shared types (mirrors server admin.types.ts) ─────────────────────────────

export interface Stage {
  stageId: number;
  accountId: number;
  stageName: string;
  stageAbbreviation: string | null;
  stageColor: string | null;
  stageOrder: number;
  active: boolean;
}

export interface AdminUser {
  userId: number;
  accountId: number;
  firstName: string;
  lastName: string;
  suffix: string | null;
  title: string | null;
  email: string;
  phone: string | null;
  userName: string;
  userLevel: number;
  role: string | null;
  active: boolean;
  primaryUser: boolean;
  signatureEnabled: boolean;
  emailSignature: string | null;
  image: string | null;
  thumb: string | null;
  qaAccess: boolean;
  gdAccess: boolean;
  lcInbox: boolean;
  ePrescribe: boolean;
  timezone: number;
  forceReset: boolean;
  gender: string | null;
  hint: string | null;
  /** Doctor NPI (aa_accounts_quest_providers, providerType='NPI'). Doctors only. */
  npi: string | null;
  loginAttempt: number;
  userRoleIds: number[];
  permissionIds: number[];
  createdAt: string | null;
  lastLoginAt: string | null;
  /**
   * Corporate sign-in state, read-only — corp-prospera-auth owns these rows.
   * `hasMasterUser` false means this person cannot sign in at all yet;
   * `mustResetPassword` true means they can, but have never done so and are
   * still holding the temporary password from the welcome e-mail. Together they
   * are how the list knows whose sign-in set-up is unfinished.
   */
  hasMasterUser?: boolean;
  mustResetPassword?: boolean;
  /** Outcome of setting up the sign-in; only present on the create response. */
  signInSetup?: string | null;
  /** Why sign-in set-up did not complete, when it did not. Create response only. */
  signInSetupReason?: string | null;
  [key: string]: unknown;
}

/**
 * A row of `aa_accounts_roles` — the per-account label override for one of the
 * built-in role types ('Doctor' / 'Coordinator' / 'Nurse' / 'Receptionist').
 * `roleTitle` is what the account calls that role and is what every user-facing
 * surface must render; `roleType` is the stable key stored on `users.role`.
 *
 * `roleId` / `accountId` / `active` are synthesized by the API from
 * `accountRoleID` / `accountID` / `endDate` — the table has no `active` column
 * and soft-deletes via the `endDate` zero-date sentinel.
 */
export interface Role {
  roleId: number;
  accountId: number;
  roleType: string;
  roleTitle: string;
  active: boolean;
  /** Zero-date sentinel ('0000-00-00 00:00:00') while the role is live. */
  endDate?: string;
}

export interface Facility {
  [key: string]: unknown;
  facilityId: number;
  accountId: number;
  facilityName: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  active: boolean;
  /**
   * IANA timezone name (e.g. "America/Los_Angeles") for this facility, with
   * the legacy `getFacilityTzone($facilityID)` fallback chain applied
   * server-side: facility.timezoneID > 0 → `country_tzones.tzName`; else the
   * account's default tz (`aa_accounts.timezone` → `country_tzones.tzName`).
   * `null` when neither resolves. Consumed by the appointment modal to
   * re-project typed status timestamps when the user changes facility — see
   * docs/audit/appt-details-parity.md §5.C.5 + P4.
   */
  ianaTimezone: string | null;
  /** Facility team member IDs (facilities_team). `0` when unassigned. */
  doctorId: number;
  coordId: number;
  nurseId: number;
}

/** Which part of a quote a discount comes off. Legacy's quote_discounts.quoteElement. */
export type DiscountScope = 'all' | 'proc' | 'or' | 'anes' | 'mat';

export interface Discount {
  discountId: number;
  accountId: number;
  name: string;
  description: string;
  discountType: 'percentage' | 'fixed';
  /** Absent on legacy rows written before the column was used; treat as 'all'. */
  quoteElement: DiscountScope;
  amount: number;
  active: boolean;
  createdAt: string | null;
}

export interface Tag {
  tagId: number;
  accountId: number;
  name: string;
  color: string | null;
  icon: string | null;
  active: boolean;
  createdAt: string | null;
}

export interface Referrer {
  [key: string]: unknown;
  referrerId: number;
  accountId: number;
  name: string;
  referrerType: string | null;
  email: string | null;
  phone: string | null;
  active: boolean;
  createdAt: string | null;
}

export interface AppointmentType {
  appointmentTypeId: number;
  accountId: number;
  name: string;
  durationMinutes: number;
  color: string | null;
  facilityId: number | null;
  facilityName?: string | null;
  resourcesRequired: boolean;
  active: boolean;
  sortOrder: number | null;
  createdAt: string | null;
}

export interface Resource {
  resourceId: number;
  accountId: number;
  resourceName: string;
  resourceType: 'doctor' | 'coordinator' | 'nurse' | 'room' | 'equipment' | 'other';
  facilityId: number | null;
  facilityName?: string | null;
  userId: number | null;
  userName?: string | null;
  active: boolean;
}

export interface AccountFeature {
  feature: string;
  label: string;
  description: string;
  enabled: boolean;
  value: string | null;
}

function parseNumber(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseNullableString(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  const text = String(value).trim();
  return text === '' ? null : text;
}

function parseBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  const text = String(value ?? '').trim().toLowerCase();
  return text === '1' || text === 'true';
}

function normalizeFacility(raw: Record<string, unknown>): Facility {
  return {
    facilityId: parseNumber(raw.facilityId ?? raw.facilityID),
    accountId: parseNumber(raw.accountId ?? raw.accountID),
    facilityName: String(raw.facilityName ?? raw.name ?? ''),
    address: parseNullableString(raw.address),
    city: parseNullableString(raw.city),
    state: parseNullableString(raw.state),
    zip: parseNullableString(raw.zip ?? raw.postalCode),
    phone: parseNullableString(raw.phone),
    active: parseBoolean(raw.active),
    ianaTimezone: parseNullableString(raw.ianaTimezone ?? raw.tzName),
    doctorId: parseNumber(raw.doctorId ?? raw.doctorID),
    coordId: parseNumber(raw.coordId ?? raw.coordID),
    nurseId: parseNumber(raw.nurseId ?? raw.nurseID),
  };
}

function normalizeReferrer(raw: Record<string, unknown>): Referrer {
  return {
    ...raw,
    referrerId: parseNumber(raw.referrerId ?? raw.referralLevel1ID ?? raw.referralLevel1Id),
    accountId: parseNumber(raw.accountId ?? raw.accountID),
    name: String(raw.name ?? ''),
    referrerType: parseNullableString(raw.referrerType ?? raw.type),
    email: parseNullableString(raw.email),
    phone: parseNullableString(raw.phone),
    active: parseBoolean(raw.active),
    createdAt: parseNullableString(raw.createdAt ?? raw.createDate),
  };
}

function normalizeAdminUser(raw: Record<string, unknown>): AdminUser {
  return {
    ...raw,
    userId: parseNumber(raw.userId ?? raw.userID),
    accountId: parseNumber(raw.accountId ?? raw.accountID),
    firstName: String(raw.firstName ?? ''),
    lastName: String(raw.lastName ?? ''),
    suffix: parseNullableString(raw.suffix),
    title: parseNullableString(raw.title),
    email: String(raw.email ?? ''),
    phone: parseNullableString(raw.phone),
    userName: String(raw.userName ?? ''),
    userLevel: parseNumber(raw.userLevel),
    role: parseNullableString(raw.role),
    active: parseBoolean(raw.active),
    primaryUser: parseBoolean(raw.primaryUser),
    signatureEnabled: parseBoolean(raw.signatureEnabled),
    emailSignature: parseNullableString(raw.emailSignature),
    image: parseNullableString(raw.image),
    thumb: parseNullableString(raw.thumb),
    qaAccess: parseBoolean(raw.qaAccess),
    gdAccess: parseBoolean(raw.gdAccess),
    lcInbox: parseBoolean(raw.lcInbox),
    ePrescribe: parseBoolean(raw.ePrescribe),
    timezone: parseNumber(raw.timezone),
    forceReset: parseBoolean(raw.forceReset),
    gender: parseNullableString(raw.gender),
    hint: parseNullableString(raw.hint),
    npi: parseNullableString(raw.npi),
    loginAttempt: parseNumber(raw.loginAttempt),
    userRoleIds: Array.isArray(raw.userRoleIds) ? (raw.userRoleIds as unknown[]).map(Number) : [],
    permissionIds: Array.isArray(raw.permissionIds) ? (raw.permissionIds as unknown[]).map(Number) : [],
    createdAt: parseNullableString(raw.createdAt ?? raw.createDate),
    lastLoginAt: parseNullableString(raw.lastLoginAt ?? raw.lastLogin),
  };
}

function normalizePlanType(raw: Record<string, unknown>): PlanType {
  return {
    planTypeId: parseNumber(raw.planTypeId ?? raw.planTypeID),
    accountId: parseNumber(raw.accountId ?? raw.accountID),
    name: String(raw.name ?? ''),
    active: parseBoolean(raw.active),
    pipelineTypeId: parseNumber(raw.pipelineTypeId ?? raw.pipelineTypeID),
  };
}

// ─── Stages ───────────────────────────────────────────────────────────────────

export function listStages(): Promise<Stage[]> {
  return apiClient
    .get<ApiResponse<Stage[]>>('/admin/stages')
    .then((r) => r.data.data);
}

// ─── Users ────────────────────────────────────────────────────────────────────

export function listUsers(): Promise<AdminUser[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/admin/users')
    .then((r) => (r.data.data ?? []).map((row) => normalizeAdminUser(row)));
}

export function getUser(id: number): Promise<AdminUser> {
  return apiClient
    .get<ApiResponse<AdminUser>>(`/admin/users/${id}`)
    .then((r) => r.data.data);
}

/**
 * What the Add User form sends.
 *
 * Spelled out rather than derived from AdminUser so the call site does not need
 * a `Record<string, unknown>` cast — a cast there erases every field and lets a
 * typo through silently.
 *
 * `password?: never` is load-bearing: sign-in is set up at login.prospera.ai and
 * the API now rejects the field outright rather than ignoring it, so a caller
 * that reintroduces one should fail to compile rather than at runtime.
 */
export interface CreateUserInput {
  firstName: string;
  lastName: string;
  email: string;
  userName: string;
  suffix?: string | null;
  title?: string | null;
  phone?: string | null;
  userLevel?: number;
  role?: string | null;
  active?: boolean;
  timezone?: number;
  signatureEnabled?: boolean;
  emailSignature?: string | null;
  roleIds?: number[];
  npi?: string;
  isAccountOwner?: boolean;
  password?: never;
}

export function createUser(data: CreateUserInput): Promise<AdminUser> {
  return apiClient
    .post<ApiResponse<Record<string, unknown>>>('/admin/users', data)
    .then((r) => normalizeAdminUser(r.data.data));
}

export function updateUser(
  id: number,
  data: Partial<Omit<AdminUser, 'userId' | 'accountId' | 'createdAt' | 'lastLoginAt'>>,
): Promise<AdminUser> {
  return apiClient
    .put<ApiResponse<Record<string, unknown>>>(`/admin/users/${id}`, data)
    .then((r) => normalizeAdminUser(r.data.data));
}

export function deactivateUser(id: number): Promise<void> {
  // Soft deactivation — sets active=false via the same PUT endpoint the
  // edit modal uses. The DELETE endpoint hard-deletes the row, which breaks
  // FK references in comm_log, plans, scheduled_appointments, etc.
  return apiClient.put(`/admin/users/${id}`, { active: false }).then(() => undefined);
}

export function reactivateUser(id: number): Promise<void> {
  return apiClient.put(`/admin/users/${id}`, { active: true }).then(() => undefined);
}

export function resetPassword(id: number, newPassword: string): Promise<void> {
  return apiClient
    .patch(`/admin/users/${id}/password`, { password: newPassword })
    .then(() => undefined);
}

export function assignRole(userId: number, roleId: number): Promise<AdminUser> {
  return apiClient
    .post<ApiResponse<AdminUser>>(`/admin/users/${userId}/roles`, { roleId })
    .then((r) => r.data.data);
}

export function removeRole(userId: number, roleId: number): Promise<AdminUser> {
  return apiClient
    .delete<ApiResponse<AdminUser>>(`/admin/users/${userId}/roles/${roleId}`)
    .then((r) => r.data.data);
}

export function getUserPermissions(userId: number): Promise<number[]> {
  return apiClient
    .get<ApiResponse<{ permissions: number[] }>>(`/admin/users/${userId}/permissions`, {
      params: { _t: Date.now() },
    })
    .then((r) => r.data.data.permissions ?? []);
}

export function updateUserPermissions(
  userId: number,
  permissions: Record<number, boolean>,
): Promise<number[]> {
  return apiClient
    .put<ApiResponse<{ permissions: number[] }>>(`/admin/users/${userId}/permissions`, { permissions })
    .then((r) => r.data.data.permissions ?? []);
}

export function uploadUserPhoto(userId: number, file: File): Promise<{ image: string; thumb: string }> {
  // uploadFile switches photos > 4 MB to the presigned-S3 flow (Lambda 6 MB cap).
  return uploadFile(`/admin/users/${userId}/photo`, file).then((r) => r.data.data);
}

/**
 * Send the sign-in e-mail again for someone who never got it. Carries no
 * password: the corp app mints and mails a fresh temporary one, which also
 * invalidates whatever was in the e-mail that went astray.
 */
export function resendUserInvite(userId: number): Promise<void> {
  return apiClient.post(`/admin/users/${userId}/invite`).then(() => undefined);
}

// ─── Roles ────────────────────────────────────────────────────────────────────

export function listRoles(): Promise<Role[]> {
  return apiClient
    .get<ApiResponse<Role[]>>('/admin/roles')
    .then((r) => r.data.data);
}

/**
 * Active custom roles from the legacy `roles` table (NOT aa_accounts_roles,
 * which only stores label overrides for the four hardcoded role types).
 *
 * Hits `GET /filters/options?types[]=group`, which is the same source legacy
 * `lib/filters/filter-assigned-to.php` and `task_items_display.php` use for
 * the role_<X> / group_<X> dropdown entries.
 *
 * Used by:
 *   - TaskModal "Assigned To" dropdown (Individual Tasks / Single Task entries)
 *   - TasksPage / TasksDashboardPage Assigned To sidebar filter options
 */
export interface AccountRoleEntry {
  roleId: number;
  roleTitle: string;
}
export function listAccountRoles(): Promise<AccountRoleEntry[]> {
  return apiClient
    .get<ApiResponse<{ group?: Array<{ value: string; label: string }> }>>(
      '/filters/options',
      { params: { 'types[]': 'group' } },
    )
    .then((r) => (r.data.data?.group ?? []).map((o) => ({
      roleId: Number(o.value),
      roleTitle: o.label,
    })));
}

/**
 * Fetch only the roles/groups the current user belongs to (via user_roles).
 * Used by the Assigned To sidebar filter on tasks pages to match legacy
 * filter-roles.php behavior.
 */
export function listMyRoles(): Promise<AccountRoleEntry[]> {
  return apiClient
    .get<ApiResponse<{ 'my-groups'?: Array<{ value: string; label: string }> }>>(
      '/filters/options',
      { params: { 'types[]': 'my-groups' } },
    )
    .then((r) => (r.data.data?.['my-groups'] ?? []).map((o) => ({
      roleId: Number(o.value),
      roleTitle: o.label,
    })));
}

export function createRole(
  data: Pick<Role, 'roleType' | 'roleTitle'> & Partial<Pick<Role, 'active'>>,
): Promise<Role> {
  return apiClient
    .post<ApiResponse<Role>>('/admin/roles', data)
    .then((r) => r.data.data);
}

export function updateRole(
  id: number,
  data: Partial<Pick<Role, 'roleType' | 'roleTitle' | 'active'>>,
): Promise<Role> {
  return apiClient
    .put<ApiResponse<Role>>(`/admin/roles/${id}`, data)
    .then((r) => r.data.data);
}

export function deleteRole(id: number): Promise<void> {
  return apiClient.delete(`/admin/roles/${id}`).then(() => undefined);
}

// ─── Facilities ───────────────────────────────────────────────────────────────

export function listFacilities(): Promise<Facility[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/admin/facilities')
    .then((r) => (r.data.data ?? []).map((row) => normalizeFacility(row)));
}

/** Team IDs are sent using the raw DB column names the API persists. */
interface FacilityTeamPayload {
  doctorID?: number;
  coordID?: number;
  nurseID?: number;
}

export function createFacility(
  data: Pick<Facility, 'facilityName'> &
    Partial<Pick<Facility, 'address' | 'city' | 'state' | 'zip' | 'phone' | 'active'>> &
    FacilityTeamPayload,
): Promise<Facility> {
  return apiClient
    .post<ApiResponse<Facility>>('/admin/facilities', data)
    .then((r) => r.data.data);
}

export function updateFacility(
  id: number,
  data: Partial<Omit<Facility, 'facilityId' | 'accountId'>> & FacilityTeamPayload,
): Promise<Facility> {
  return apiClient
    .put<ApiResponse<Facility>>(`/admin/facilities/${id}`, data)
    .then((r) => r.data.data);
}

export function deleteFacility(id: number): Promise<void> {
  return apiClient.delete(`/admin/facilities/${id}`).then(() => undefined);
}

// ─── Discounts ────────────────────────────────────────────────────────────────

export function listDiscounts(): Promise<Discount[]> {
  return apiClient
    .get<ApiResponse<Discount[]>>('/admin/discounts')
    .then((r) => r.data.data);
}

export function createDiscount(
  data: Pick<Discount, 'name' | 'discountType' | 'amount'>
    & Partial<Pick<Discount, 'active' | 'description' | 'quoteElement'>>,
): Promise<Discount> {
  return apiClient
    .post<ApiResponse<Discount>>('/admin/discounts', data)
    .then((r) => r.data.data);
}

export function updateDiscount(
  id: number,
  data: Partial<Pick<Discount,
    'name' | 'discountType' | 'amount' | 'active' | 'description' | 'quoteElement'>>,
): Promise<Discount> {
  return apiClient
    .put<ApiResponse<Discount>>(`/admin/discounts/${id}`, data)
    .then((r) => r.data.data);
}

export function deleteDiscount(id: number): Promise<void> {
  return apiClient.delete(`/admin/discounts/${id}`).then(() => undefined);
}

// ─── Tags Custom (Patient Tags) ──────────────────────────────────────────────

export interface TagCustom {
  tagsCustomID: number;
  accountID: number;
  title: string;
  icon: string;
  active: string; // '1' or '0'
}

export function listTagsCustom(): Promise<TagCustom[]> {
  return apiClient
    .get<ApiResponse<TagCustom[]>>('/tags-custom', { params: { active: true } })
    .then((r) => r.data.data);
}

// ─── Referrers ────────────────────────────────────────────────────────────────

export function listReferrers(): Promise<Referrer[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/admin/referrers')
    .then((r) => (r.data.data ?? []).map((row) => normalizeReferrer(row)));
}

// ─── Appointment Types ────────────────────────────────────────────────────────

export function listAppointmentTypes(): Promise<AppointmentType[]> {
  return apiClient
    .get<ApiResponse<AppointmentType[]>>('/admin/appointment-types')
    .then((r) => r.data.data);
}

export function createAppointmentType(
  data: Pick<AppointmentType, 'name' | 'durationMinutes'> &
    Partial<Pick<AppointmentType, 'color' | 'facilityId' | 'resourcesRequired' | 'sortOrder' | 'active'>>,
): Promise<AppointmentType> {
  return apiClient
    .post<ApiResponse<AppointmentType>>('/admin/appointment-types', data)
    .then((r) => r.data.data);
}

export function updateAppointmentType(
  id: number,
  data: Partial<
    Pick<AppointmentType, 'name' | 'durationMinutes' | 'color' | 'facilityId' | 'resourcesRequired' | 'sortOrder' | 'active'>
  >,
): Promise<AppointmentType> {
  return apiClient
    .put<ApiResponse<AppointmentType>>(`/admin/appointment-types/${id}`, data)
    .then((r) => r.data.data);
}

export function deleteAppointmentType(id: number): Promise<void> {
  return apiClient.delete(`/admin/appointment-types/${id}`).then(() => undefined);
}

// ─── Resources ────────────────────────────────────────────────────────────────

export function listResources(): Promise<Resource[]> {
  return apiClient
    .get<ApiResponse<Resource[]>>('/admin/resources')
    .then((r) => r.data.data);
}

export function createResource(
  data: Pick<Resource, 'resourceName' | 'resourceType'> &
    Partial<Pick<Resource, 'facilityId' | 'userId' | 'active'>>,
): Promise<Resource> {
  return apiClient
    .post<ApiResponse<Resource>>('/admin/resources', data)
    .then((r) => r.data.data);
}

export function updateResource(
  id: number,
  data: Partial<Pick<Resource, 'resourceName' | 'resourceType' | 'facilityId' | 'userId' | 'active'>>,
): Promise<Resource> {
  return apiClient
    .put<ApiResponse<Resource>>(`/admin/resources/${id}`, data)
    .then((r) => r.data.data);
}

export function deleteResource(id: number): Promise<void> {
  return apiClient.delete(`/admin/resources/${id}`).then(() => undefined);
}

// ─── Languages ───────────────────────────────────────────────────────────────

export interface Language {
  languageId: number;
  name: string;
  orderNumber: number;
  active: boolean;
  hint: string | null;
}

export function listLanguages(): Promise<Language[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/admin/languages')
    .then((r) =>
      (r.data.data ?? []).map((row) => ({
        languageId: Number(row.languageID ?? row.languageId ?? 0),
        name: String(row.name ?? ''),
        // The column is orderID; orderNumber is this type's name for it.
        orderNumber: Number(row.orderID ?? row.orderNumber ?? 0),
        active: row.active === true || row.active === 1 || row.active === '1',
        hint: row.hint != null ? String(row.hint) : null,
      }))
      .sort((a, b) => a.orderNumber - b.orderNumber),
    );
}

/**
 * The languages row as the generic admin route writes it: every column is NOT
 * NULL, so a missing hint is '' (null failed the insert) and a new language is
 * active (no active left it 0, so it saved but the list, which shows active
 * languages only, never showed it).
 */
function languageRow(data: Partial<Pick<Language, 'name' | 'orderNumber' | 'active' | 'hint'>>): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  if (data.name !== undefined) row.name = data.name;
  if ('hint' in data) row.hint = data.hint ?? '';
  if (data.orderNumber !== undefined) row.orderID = data.orderNumber;
  if (data.active !== undefined) row.active = data.active ? 1 : 0;
  return row;
}

export function createLanguage(
  data: Pick<Language, 'name'> & Partial<Pick<Language, 'orderNumber' | 'active' | 'hint'>>,
): Promise<Language> {
  return apiClient
    .post<ApiResponse<Language>>('/admin/languages', {
      hint: '',
      orderID: 0,
      active: 1,
      ...languageRow(data),
    })
    .then((r) => r.data.data);
}

export function updateLanguage(
  id: number,
  data: Partial<Pick<Language, 'name' | 'orderNumber' | 'active' | 'hint'>>,
): Promise<Language> {
  return apiClient
    .put<ApiResponse<Language>>(`/admin/languages/${id}`, languageRow(data))
    .then((r) => r.data.data);
}

export function deleteLanguage(id: number): Promise<void> {
  return apiClient.delete(`/admin/languages/${id}`).then(() => undefined);
}

// ─── Cancellation Reasons ────────────────────────────────────────────────────

export interface CancellationReason {
  cancellationReasonId: number;
  title: string;
  active: boolean;
  orderNumber: number;
}

export function listCancellationReasons(): Promise<CancellationReason[]> {
  return apiClient
    .get<ApiResponse<CancellationReason[]>>('/admin/cancellation-reasons')
    .then((r) => r.data.data);
}

export function createCancellationReason(
  data: Pick<CancellationReason, 'title'> & Partial<Pick<CancellationReason, 'orderNumber' | 'active'>>,
): Promise<CancellationReason> {
  return apiClient
    .post<ApiResponse<CancellationReason>>('/admin/cancellation-reasons', data)
    .then((r) => r.data.data);
}

export function updateCancellationReason(
  id: number,
  data: Partial<Pick<CancellationReason, 'title' | 'orderNumber' | 'active'>>,
): Promise<CancellationReason> {
  return apiClient
    .put<ApiResponse<CancellationReason>>(`/admin/cancellation-reasons/${id}`, data)
    .then((r) => r.data.data);
}

export function deleteCancellationReason(id: number): Promise<void> {
  return apiClient.delete(`/admin/cancellation-reasons/${id}`).then(() => undefined);
}

// ─── QA Reasons ──────────────────────────────────────────────────────────────

export interface QaReason {
  qaReasonId: number;
  title: string;
  createDate: string | null;
  endDate: string | null;
  orderNumber: number;
}

export function listQaReasons(): Promise<QaReason[]> {
  // The /admin/qa-reasons endpoint returns raw DB rows whose primary key is
  // `qaReasonID` (capital ID). Normalize to the declared `qaReasonId` shape so
  // callers (e.g. the QA case modal checkbox grid) read a defined id instead of
  // `undefined` for every reason.
  return apiClient
    .get<ApiResponse<Array<Record<string, unknown>>>>('/admin/qa-reasons')
    .then((r) =>
      (r.data.data ?? []).map((row) => ({
        qaReasonId: Number(row.qaReasonID ?? row.qaReasonId ?? 0),
        title: String(row.title ?? ''),
        createDate: (row.createDate as string | null) ?? null,
        endDate: (row.endDate as string | null) ?? null,
        orderNumber: Number(row.orderNumber ?? 0),
      })),
    );
}

export function createQaReason(
  data: Pick<QaReason, 'title'> & Partial<Pick<QaReason, 'orderNumber'>>,
): Promise<QaReason> {
  return apiClient
    .post<ApiResponse<QaReason>>('/admin/qa-reasons', data)
    .then((r) => r.data.data);
}

export function updateQaReason(
  id: number,
  data: Partial<Pick<QaReason, 'title' | 'orderNumber'>>,
): Promise<QaReason> {
  return apiClient
    .put<ApiResponse<QaReason>>(`/admin/qa-reasons/${id}`, data)
    .then((r) => r.data.data);
}

export function deleteQaReason(id: number): Promise<void> {
  return apiClient.delete(`/admin/qa-reasons/${id}`).then(() => undefined);
}

// ─── Patient Interests ───────────────────────────────────────────────────────

export interface PatientInterest {
  patientInterestId: number;
  planTypeId: number;
  interestTitle: string;
  interestId: number;
  orderNumber: number;
  endDate: string | null;
}

export function listPatientInterests(): Promise<PatientInterest[]> {
  return apiClient
    .get<ApiResponse<PatientInterest[]>>('/admin/patient-interests')
    .then((r) => r.data.data);
}

export function createPatientInterest(
  data: Pick<PatientInterest, 'interestTitle'> & Partial<Pick<PatientInterest, 'orderNumber' | 'planTypeId' | 'interestId'>>,
): Promise<PatientInterest> {
  return apiClient
    .post<ApiResponse<PatientInterest>>('/admin/patient-interests', data)
    .then((r) => r.data.data);
}

export function updatePatientInterest(
  id: number,
  data: Partial<Pick<PatientInterest, 'interestTitle' | 'orderNumber' | 'planTypeId' | 'interestId'>>,
): Promise<PatientInterest> {
  return apiClient
    .put<ApiResponse<PatientInterest>>(`/admin/patient-interests/${id}`, data)
    .then((r) => r.data.data);
}

export function deletePatientInterest(id: number): Promise<void> {
  return apiClient.delete(`/admin/patient-interests/${id}`).then(() => undefined);
}

// ─── Print Templates ─────────────────────────────────────────────────────────

export interface PrintTemplate {
  printTemplateId: number;
  name: string;
  content: string | null;
  header: string | null;
  footer: string | null;
  active: boolean;
  sortOrder: number;
}

export function listPrintTemplates(): Promise<PrintTemplate[]> {
  return apiClient
    .get<ApiResponse<PrintTemplate[]>>('/admin/print-templates')
    .then((r) => r.data.data);
}

export function createPrintTemplate(
  data: Pick<PrintTemplate, 'name'> &
    Partial<Pick<PrintTemplate, 'content' | 'header' | 'footer' | 'active' | 'sortOrder'>>,
): Promise<PrintTemplate> {
  return apiClient
    .post<ApiResponse<PrintTemplate>>('/admin/print-templates', data)
    .then((r) => r.data.data);
}

export function updatePrintTemplate(
  id: number,
  data: Partial<Pick<PrintTemplate, 'name' | 'content' | 'header' | 'footer' | 'active' | 'sortOrder'>>,
): Promise<PrintTemplate> {
  return apiClient
    .put<ApiResponse<PrintTemplate>>(`/admin/print-templates/${id}`, data)
    .then((r) => r.data.data);
}

export function deletePrintTemplate(id: number): Promise<void> {
  return apiClient.delete(`/admin/print-templates/${id}`).then(() => undefined);
}

// ─── Feature Flags ────────────────────────────────────────────────────────────

export function getFeatures(): Promise<AccountFeature[]> {
  return apiClient
    .get<ApiResponse<AccountFeature[]>>('/admin/features')
    .then((r) => r.data.data);
}

export function setFeature(
  feature: string,
  data: { enabled: boolean; value?: string | null },
): Promise<AccountFeature[]> {
  return apiClient
    .patch<ApiResponse<AccountFeature[]>>(`/admin/features/${feature}`, data)
    .then((r) => r.data.data);
}

// ─── Billing Settings ─────────────────────────────────────────────────────────

export interface BillingSettings {
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  planType: string;
  billingCycle: string;
  paymentMethod: string;
}

export function getBilling(): Promise<BillingSettings> {
  return apiClient
    .get<ApiResponse<BillingSettings>>('/admin/billing')
    .then((r) => r.data.data);
}

export function updateBilling(data: Partial<BillingSettings>): Promise<BillingSettings> {
  return apiClient
    .put<ApiResponse<BillingSettings>>('/admin/billing', data)
    .then((r) => r.data.data);
}

// ─── Access Restrictions (legacy admin_ip_addresses.php) ──────────────────────
// Two shared legacy tables:
//   ip_enabled → the per-account "Restrict For" tier flags (one row/account).
//   ip_allowed → the exception list; `address` is a dotted IPv4 OR "UID|<userId>".

export interface AccessEnabled {
  enabled: number;
  phoneCoords: number;
  sysUsers: number;
  sysAdmins: number;
  accountID?: number;
  ipEnabledID?: number;
}

export interface AccessRestrictionRow {
  ipAllowedID: number;
  address: string;
  description: string;
  accountID: number;
}

export function getAccessEnabled(): Promise<AccessEnabled> {
  return apiClient
    .get<ApiResponse<AccessEnabled>>('/admin/access-enabled')
    .then((r) => r.data.data);
}

export function updateAccessEnabled(data: AccessEnabled): Promise<AccessEnabled> {
  return apiClient
    .put<ApiResponse<AccessEnabled>>('/admin/access-enabled', data)
    .then((r) => r.data.data);
}

export function listAccessRestrictions(): Promise<AccessRestrictionRow[]> {
  return apiClient
    .get<ApiResponse<AccessRestrictionRow[]>>('/admin/access-restrictions-list')
    .then((r) => r.data.data);
}

export function createAccessRestriction(data: {
  address: string;
  description: string;
}): Promise<AccessRestrictionRow> {
  return apiClient
    .post<ApiResponse<AccessRestrictionRow>>('/admin/access-restrictions-list', data)
    .then((r) => r.data.data);
}

export function deleteAccessRestriction(id: number): Promise<void> {
  return apiClient.delete(`/admin/access-restrictions-list/${id}`).then(() => undefined);
}

// ─── Merchant Accounts ────────────────────────────────────────────────────

export interface SubscribeMerchant {
  subscribeMerchantId: number;
  accountId: number;
  friendlyTitle: string;
  publicApiKey: string | null;
  privateApiKey: string | null;
  facilityId: number | null;
  isDefault: boolean;
  active: boolean;
  dateCreated: string | null;
  dateModified: string | null;
}

export function listMerchantAccounts(): Promise<SubscribeMerchant[]> {
  return apiClient
    .get<ApiResponse<SubscribeMerchant[]>>('/admin/merchant-accounts')
    .then((r) => r.data.data);
}

export function createMerchantAccount(
  data: Omit<SubscribeMerchant, 'subscribeMerchantId' | 'accountId' | 'dateCreated' | 'dateModified'>,
): Promise<SubscribeMerchant> {
  return apiClient
    .post<ApiResponse<SubscribeMerchant>>('/admin/merchant-accounts', data)
    .then((r) => r.data.data);
}

export function updateMerchantAccount(
  id: number,
  data: Partial<Omit<SubscribeMerchant, 'subscribeMerchantId' | 'accountId' | 'dateCreated' | 'dateModified'>>,
): Promise<SubscribeMerchant> {
  return apiClient
    .put<ApiResponse<SubscribeMerchant>>(`/admin/merchant-accounts/${id}`, data)
    .then((r) => r.data.data);
}

export function deleteMerchantAccount(id: number): Promise<void> {
  return apiClient.delete(`/admin/merchant-accounts/${id}`).then(() => undefined);
}

// ─── Forward Merchants ────────────────────────────────────────────────────
//
// The practice's own card-processing merchant accounts (aa_accounts_forward),
// managed under Admin > Payments > Forward. Not to be confused with the
// Merchant Accounts block above, which is Prospera's own billing merchant.
//
// The Forward API credentials are system-level and deliberately absent from
// this type: the API never returns them and no call here sends them.

export interface ForwardMerchant {
  accountForwardID: number;
  friendlyTitle: string;
  /** Forward's own account identifier. Fixed once the merchant exists. */
  account_id: string;
  facilityID: number | null;
  facilityName: string | null;
  isDefault: boolean;
  active: boolean;
}

export interface ForwardMerchantInput {
  friendlyTitle: string;
  facilityID: number | null;
  isDefault: boolean;
}

/** Active merchants only, unless `includeInactive` asks for the rest too. */
export function listForwardMerchants(includeInactive = false): Promise<ForwardMerchant[]> {
  return apiClient
    .get<ApiResponse<ForwardMerchant[]>>('/admin/forward-merchants', {
      params: includeInactive ? { include_inactive: 1 } : undefined,
    })
    .then((r) => r.data.data ?? []);
}

export function createForwardMerchant(
  data: ForwardMerchantInput & { account_id: string },
): Promise<ForwardMerchant> {
  return apiClient
    .post<ApiResponse<ForwardMerchant>>('/admin/forward-merchants', data)
    .then((r) => r.data.data);
}

/** `account_id` is intentionally not accepted: the API refuses to change it. */
export function updateForwardMerchant(
  id: number,
  data: Partial<ForwardMerchantInput>,
): Promise<ForwardMerchant> {
  return apiClient
    .put<ApiResponse<ForwardMerchant>>(`/admin/forward-merchants/${id}`, data)
    .then((r) => r.data.data);
}

/** Soft delete — the row stays, `active` goes to false. */
export function deactivateForwardMerchant(id: number): Promise<void> {
  return apiClient.delete(`/admin/forward-merchants/${id}`).then(() => undefined);
}

export function restoreForwardMerchant(id: number): Promise<void> {
  return apiClient.put(`/admin/forward-merchants/${id}/restore`).then(() => undefined);
}

/**
 * Legacy "Transfer Memberships": every active membership charged through
 * `fromId` is charged through `toId` from now on. Answers how many moved.
 * Cannot be undone. Needs Admin Access.
 */
export function transferForwardMemberships(fromId: number, toId: number): Promise<number> {
  return apiClient
    .post<ApiResponse<{ count: number }>>('/admin/forward-merchants/transfer-memberships', { fromId, toId })
    .then((r) => Number(r.data.data?.count) || 0);
}

// ─── User Groups ──────────────────────────────────────────────────────────
// Backed by the legacy `roles` table (not aa_accounts_roles).
// Soft-delete via endDate: '0000-00-00 00:00:00' = active, any real datetime = deleted.

interface RawUserGroup {
  roleID: number;
  accountID: number;
  title: string;
  roleType: string;
  endDate: string;
  endUserID: string;
  createDate: string;
  createUserID: string;
  rOrder: number;
}

export interface UserGroup {
  roleId: number;
  accountId: number;
  name: string;
  active: boolean;
  rOrder: number;
}

const ZERO_DATE = '0000-00-00 00:00:00';

function toUserGroup(raw: RawUserGroup): UserGroup {
  return {
    roleId: Number(raw.roleID),
    accountId: Number(raw.accountID),
    name: raw.title ?? '',
    active: raw.endDate === ZERO_DATE,
    rOrder: Number(raw.rOrder) || 0,
  };
}

export function listUserGroups(): Promise<UserGroup[]> {
  return apiClient
    .get<ApiResponse<RawUserGroup[]>>('/admin/user-groups')
    .then((r) => (r.data.data ?? []).map(toUserGroup));
}

export function createUserGroup(name: string): Promise<{ id: number }> {
  return apiClient
    .post<ApiResponse<{ id: number }>>('/admin/user-groups', {
      title: name,
      roleType: '0',
      endDate: ZERO_DATE,
      endUserID: '0',
      createDate: new Date().toISOString().slice(0, 19).replace('T', ' '),
      createUserID: '0',
      rOrder: 0,
    })
    .then((r) => r.data.data);
}

export function updateUserGroup(
  id: number,
  data: { name: string },
): Promise<void> {
  return apiClient
    .put(`/admin/user-groups/${id}`, { title: data.name })
    .then(() => undefined);
}

export function softDeleteUserGroup(id: number): Promise<void> {
  return apiClient
    .put(`/admin/user-groups/${id}`, {
      endDate: new Date().toISOString().slice(0, 19).replace('T', ' '),
    })
    .then(() => undefined);
}

export function restoreUserGroup(id: number): Promise<void> {
  return apiClient
    .put(`/admin/user-groups/${id}`, { endDate: ZERO_DATE })
    .then(() => undefined);
}

export function listGroupMembers(groupId: number): Promise<number[]> {
  return apiClient
    .get<ApiResponse<number[]>>(`/admin/user-groups/${groupId}/members`)
    .then((r) => r.data.data ?? []);
}

export function syncGroupMembers(groupId: number, userIds: number[]): Promise<void> {
  return apiClient
    .put(`/admin/user-groups/${groupId}/members`, { userIds })
    .then(() => undefined);
}

export function listGroupPermissions(groupId: number): Promise<number[]> {
  return apiClient
    .get<ApiResponse<number[]>>(`/admin/user-groups/${groupId}/permissions`)
    .then((r) => r.data.data ?? []);
}

export function syncGroupPermissions(groupId: number, permissionIds: number[]): Promise<void> {
  return apiClient
    .put(`/admin/user-groups/${groupId}/permissions`, { permissionIds })
    .then(() => undefined);
}

// ─── Plan Views ──────────────────────────────────────────────────────────────

export interface PlanView {
  plansListViewId: number;
  accountId: number;
  name: string;
  column1: number;
  column2: number;
  column3: number;
  column4: number;
  column5: number;
  column6: number;
  column7: number;
  column8: number;
  column9: number;
  column10: number;
  column11: number;
  defaultView: number;
}

export function listPlanViews(): Promise<PlanView[]> {
  return apiClient
    .get<ApiResponse<PlanView[]>>('/admin/plan-views')
    .then((r) => r.data.data);
}

export function createPlanView(
  data: Pick<PlanView, 'name'> &
    Partial<Omit<PlanView, 'plansListViewId' | 'accountId' | 'name'>>,
): Promise<PlanView> {
  return apiClient
    .post<ApiResponse<PlanView>>('/admin/plan-views', data)
    .then((r) => r.data.data);
}

export function updatePlanView(
  id: number,
  data: Partial<Omit<PlanView, 'plansListViewId' | 'accountId'>>,
): Promise<PlanView> {
  return apiClient
    .put<ApiResponse<PlanView>>(`/admin/plan-views/${id}`, data)
    .then((r) => r.data.data);
}

export function deletePlanView(id: number): Promise<void> {
  return apiClient.delete(`/admin/plan-views/${id}`).then(() => undefined);
}

// ─── Appointment Confirmations ───────────────────────────────────────────────

export interface AppointmentConfirmation {
  appointmentConfirmTypeID: number;
  name: string;
  message: string;
  sms_message: string;
  send_type: number;
  day1: boolean;
  day2: boolean;
  day3: boolean;
  day4: boolean;
  day5: boolean;
  day6: boolean;
  day7: boolean;
  day8: boolean;
  continue_confirm: boolean;
  prompt_confirm: boolean;
  /**
   * Optional override of the account's booking-acknowledgement text for this
   * appointment type group. Empty = use the account body
   * (ConfirmationSettings.apptBookedSmsMessage).
   */
  booked_sms_message: string;
  active: boolean;
}

export interface ConfirmationSettings {
  apptConfirmTime: string;
  apptEmailConfirmTime: string;
  /**
   * 1 = one confirmation per patient/facility/day showing the day's first
   * appointment as the arrival time (the legacy default). 0 = every appointment
   * sends its own confirmation showing its own time. Legacy TINYINT(1).
   */
  apptConfirmUseArrivalTime: number;
  /**
   * "Your appointment is booked" text, sent the moment an appointment is
   * created — separate from the reminders above, which follow the day schedule.
   * 0 = off, 1 = patient self-scheduled bookings only, 2 = staff bookings too.
   */
  apptBookedSmsSend: number;
  /** Body for the above. Supports the same `${Token}` names as the SMS reminder. */
  apptBookedSmsMessage: string;
  /**
   * Read-only. What gets saved and sent if the practice turns the booking text
   * on without writing any — server-owned, so the page shows the real wording
   * rather than inventing its own copy. Ignored on write.
   */
  apptBookedSmsDefaultMessage: string;
  /**
   * Thank-you text sent back when a guest replies YES to a confirmation text.
   * 0 = off (the default), 1 = on.
   */
  apptConfirmedReplySmsSend: number;
  /**
   * Body for the above. Read-only in this app: authored in the corp dashboard.
   * '' = the sender uses apptConfirmedReplySmsDefaultMessage (not seeded into
   * the row on save, unlike the booking text).
   */
  apptConfirmedReplySmsMessage: string;
  /** Read-only, server-owned. The wording sent when no custom wording is set. */
  apptConfirmedReplySmsDefaultMessage: string;
  /**
   * 1 = text the patient the GI form link when it hasn't been filled out, at
   * booking and on reminder days (at most once a day). 0 = off. Independent of
   * apptBookedSmsSend. Enabling without a GI form set is refused with a 422
   * (code GI_FORM_NOT_CONFIGURED).
   */
  apptGiFormSms: number;
  /** Read-only. Whether the practice has a GI form set. Ignored on write. */
  giFormConfigured: boolean;
  /**
   * Read-only. The GI text's fixed wording with `<Practice name>` and `<link>`
   * placeholders, from the service that sends it. Ignored on write.
   */
  apptGiFormSmsWording: string;
  accountTimezone: string | null;
}

export interface AppointmentConfirmationListResult {
  items: AppointmentConfirmation[];
  accountTimezone: string | null;
}

/** Active confirmations; `includeDeleted` adds the deleted (inactive) ones too. */
export function listAppointmentConfirmations({ includeDeleted = false }: { includeDeleted?: boolean } = {}): Promise<AppointmentConfirmationListResult> {
  return apiClient
    .get<ApiResponse<AppointmentConfirmation[]>>('/appointment-confirm-types', {
      params: includeDeleted ? { include_inactive: 1 } : undefined,
    })
    .then((r) => ({
      items: r.data.data,
      accountTimezone: (r.data.meta?.accountTimezone as string) ?? null,
    }));
}

/** What the corp dashboard's confirmation form sends (no booking-text override). */
export type AppointmentConfirmationPayload = Pick<
  AppointmentConfirmation,
  | 'name'
  | 'message'
  | 'sms_message'
  | 'send_type'
  | 'day1'
  | 'day2'
  | 'day3'
  | 'day4'
  | 'day5'
  | 'day6'
  | 'day7'
  | 'day8'
  | 'continue_confirm'
  | 'prompt_confirm'
>;

export function createAppointmentConfirmation(data: AppointmentConfirmationPayload): Promise<AppointmentConfirmation> {
  return apiClient
    .post<ApiResponse<AppointmentConfirmation>>('/appointment-confirm-types', data)
    .then((r) => r.data.data);
}

export function updateAppointmentConfirmation(id: number, data: AppointmentConfirmationPayload): Promise<void> {
  return apiClient.put(`/appointment-confirm-types/${id}`, data).then(() => undefined);
}

/** Soft: the confirmation turns inactive and shows under Show deleted. */
export function deleteAppointmentConfirmation(id: number): Promise<void> {
  return apiClient.delete(`/appointment-confirm-types/${id}`).then(() => undefined);
}

export function restoreAppointmentConfirmation(id: number): Promise<void> {
  return apiClient.post(`/appointment-confirm-types/${id}/restore`).then(() => undefined);
}

export function getConfirmationSettings(): Promise<ConfirmationSettings> {
  return apiClient
    .get<ApiResponse<ConfirmationSettings>>('/appointment-confirm-types/settings')
    .then((r) => r.data.data);
}

export function updateConfirmationSettings(
  data: Partial<ConfirmationSettings>,
): Promise<ConfirmationSettings> {
  return apiClient
    .put<ApiResponse<ConfirmationSettings>>('/appointment-confirm-types/settings', data)
    .then((r) => r.data.data);
}

// ─── Calendar Settings ───────────────────────────────────────────────────────

/** The 18 `aa_accounts_calendar` Info Pop-up flags plus `displayDailyAlert`. */
export interface CalendarInfoFlags {
  infoTags: number;
  infoPhoto: number;
  infoDob: number;
  infoPhone: number;
  infoChartId: number;
  infoStatus: number;
  infoPlanStage: number;
  infoCheckedInWait: number;
  infoCheckedBackWait: number;
  infoApptName: number;
  infoApptType: number;
  infoFacility: number;
  infoPatientNotes: number;
  infoApptNotes: number;
  infoResources: number;
  infoDoctor: number;
  infoCoordinator: number;
  infoMemberships: number;
  displayDailyAlert: number;
}

/** `aa_accounts` scheduler appearance columns. */
export interface CalendarAppearance {
  draggableAppts: number;
  statusColors: number;
  schedulerGradients: number;
  schedulerResources: number;
  /** Stored with the unit, e.g. `'14px'` — legacy keeps the suffix. */
  schedulerFontSize: string | null;
}

/** `aa_accounts` status border colors, normalized to `#rrggbb` by the API. */
export interface CalendarStatusColorPalette {
  scheduled: string | null;
  confirmed: string | null;
  checkedIn: string | null;
  checkedBack: string | null;
  checkedOut: string | null;
  noShow: string | null;
  waitList: string | null;
  cancelled: string | null;
  contactAttempted: string | null;
}

/** The 6 fixed `aa_accounts.apptChangeRule*` slots. Values are `'0' | '1'`, and `'2'` for rule 5. */
export type CalendarApptChangeRules = Record<
  'apptChangeRule1' | 'apptChangeRule2' | 'apptChangeRule3' | 'apptChangeRule4' | 'apptChangeRule5' | 'apptChangeRule6',
  string
>;

/** One "Additional Rules" row (`appointment_stage_rules`). */
export interface CalendarStageRule {
  appointmentStageRuleID: number;
  standardType: string;
  appointmentTypeGroupID: number;
  /**
   * Single value for the type select: a built-in class slug (`consult`, …) or a
   * numeric `appointmentTypeGroupID` as a string. The API splits this back into
   * `standardType` / `appointmentTypeGroupID` on save.
   */
  type: string;
  apptAction: string;
  stageID: number;
}

/** Option lists for the two rule sections' selects. */
export interface CalendarSettingsOptions {
  standardTypes: { value: string; label: string }[];
  appointmentTypeGroups: { appointmentTypeGroupID: number; name: string }[];
  stages: { value: number; label: string }[];
  /** Stage id → display title, for the 6 fixed rules' option labels. */
  fixedRuleStageTitles: Record<string, string>;
}

/**
 * Note the `info*` flags stay at the top level: `SchedulePage` reads this same
 * endpoint for appointment-card field visibility and indexes the response by
 * legacy column name.
 */
export interface CalendarSettings extends CalendarInfoFlags {
  accountCalendarID: number | null;
  accountID: number;
  appearance: CalendarAppearance;
  statusColorPalette: CalendarStatusColorPalette;
  apptChangeRules: CalendarApptChangeRules;
  stageRules: CalendarStageRule[];
  /**
   * Only present when requested. The select option lists cost four extra queries
   * to assemble, and the scheduler — which reads this same endpoint on every page
   * load for card field visibility — does not use them.
   */
  options?: CalendarSettingsOptions;
}

/** The admin screen's view, where the option lists are guaranteed. */
export interface CalendarSettingsWithOptions extends CalendarSettings {
  options: CalendarSettingsOptions;
}

/** What `PUT /admin/calendar-settings` accepts — every section is optional. */
export interface CalendarSettingsUpdate extends Partial<CalendarInfoFlags> {
  appearance?: Partial<CalendarAppearance>;
  statusColorPalette?: Partial<CalendarStatusColorPalette>;
  apptChangeRules?: Partial<CalendarApptChangeRules>;
  stageRules?: (Pick<CalendarStageRule, 'type' | 'apptAction' | 'stageID'> & {
    appointmentStageRuleID?: number;
  })[];
}

/**
 * Field-visibility flags plus appearance, palette and rules — without the admin
 * select option lists. This is what the scheduler needs.
 */
export function getCalendarSettings(): Promise<CalendarSettings> {
  return apiClient
    .get<ApiResponse<CalendarSettings>>('/admin/calendar-settings')
    .then((r) => r.data.data);
}

/** The same payload plus the option lists the admin screen's selects need. */
export function getCalendarSettingsWithOptions(): Promise<CalendarSettingsWithOptions> {
  return apiClient
    .get<ApiResponse<CalendarSettingsWithOptions>>('/admin/calendar-settings', {
      params: { includeOptions: 1 },
    })
    .then((r) => r.data.data);
}

/** Returns the re-read settings so callers can reset their dirty baseline. */
export function updateCalendarSettings(
  data: CalendarSettingsUpdate,
): Promise<CalendarSettingsWithOptions> {
  return apiClient
    .put<ApiResponse<CalendarSettingsWithOptions>>('/admin/calendar-settings', data)
    .then((r) => r.data.data);
}

// ─── Gift Card Settings ───────────────────────────────────────────────────────

/**
 * One `gift_card_settings` row per account (online gift cards). An account
 * that has never saved reads the defaults: no presets, no limits (0 means no
 * limit on that side), no product, both online switches off.
 */
export interface GiftCardSettings {
  accountID: number;
  presetAmounts: number[];
  minAmount: number;
  maxAmount: number;
  redemptionTerms: string;
  /** The account's gift card product (a `productType = 1` quote item); 0 = none. */
  quoteItemCostID: number;
  onlineSalesEnabled: boolean;
  onlineRedemptionEnabled: boolean;
  options?: GiftCardSettingsOptions;
}

export interface GiftCardSettingsOptions {
  products: { quoteItemCostID: number; name: string; active: boolean }[];
}

export interface GiftCardSettingsWithOptions extends GiftCardSettings {
  options: GiftCardSettingsOptions;
}

/** What `PUT /admin/gift-card-settings` accepts — every field is optional. */
export type GiftCardSettingsUpdate = Partial<Omit<GiftCardSettings, 'accountID' | 'options'>>;

export function getGiftCardSettings(): Promise<GiftCardSettings> {
  return apiClient
    .get<ApiResponse<GiftCardSettings>>('/admin/gift-card-settings')
    .then((r) => r.data.data);
}

/** The settings plus the product picker's options, for the admin page. */
export function getGiftCardSettingsWithOptions(): Promise<GiftCardSettingsWithOptions> {
  return apiClient
    .get<ApiResponse<GiftCardSettingsWithOptions>>('/admin/gift-card-settings', {
      params: { includeOptions: 1 },
    })
    .then((r) => r.data.data);
}

/** Returns the re-read settings so callers can reset their dirty baseline. */
export function updateGiftCardSettings(
  data: GiftCardSettingsUpdate,
): Promise<GiftCardSettingsWithOptions> {
  return apiClient
    .put<ApiResponse<GiftCardSettingsWithOptions>>('/admin/gift-card-settings', data)
    .then((r) => r.data.data);
}

// ─── Task Groups ──────────────────────────────────────────────────────────────

export interface TaskGroup {
  taskGroupId: number;
  accountId: number;
  name: string;
  required: boolean;
  active: boolean;
  planTypeId: number | null;
  doctorId: number | null;
}

export function listTaskGroups(): Promise<TaskGroup[]> {
  return apiClient
    .get<ApiResponse<TaskGroup[]>>('/admin/task-groups')
    .then((r) => r.data.data);
}

export function createTaskGroup(
  data: Pick<TaskGroup, 'name'> & Partial<Pick<TaskGroup, 'required' | 'active'>>,
): Promise<TaskGroup> {
  return apiClient
    .post<ApiResponse<TaskGroup>>('/admin/task-groups', data)
    .then((r) => r.data.data);
}

export function updateTaskGroup(
  id: number,
  data: Partial<Pick<TaskGroup, 'name' | 'required' | 'active'>>,
): Promise<TaskGroup> {
  return apiClient
    .put<ApiResponse<TaskGroup>>(`/admin/task-groups/${id}`, data)
    .then((r) => r.data.data);
}

export function deleteTaskGroup(id: number): Promise<void> {
  return apiClient.delete(`/admin/task-groups/${id}`).then(() => undefined);
}

// ─── New Patient Form settings ────────────────────────────────────────────────

/** One choice a field's default may take; `value` is the stored id or code. */
export interface NewPatientFormDefaultOption {
  value: string;
  label: string;
}

/** GET/PUT /admin/new-patient-form. Fields without an input yet are not listed. */
export interface NewPatientFormAdminDocument {
  hintsEnabled: boolean;
  fields: ResolvedField[];
  /** fieldKey => its default choices, for fields that accept a default. */
  defaultOptions: Record<string, NewPatientFormDefaultOption[]>;
}

/** One field as the admin PUT takes it. Empty label/hint/default clear them. */
export interface NewPatientFormFieldInput {
  key: string;
  order: number;
  label: string;
  hint: string;
  default: string;
  modes: Record<FormMode, FieldModeState>;
}

export interface NewPatientFormAdminInput {
  hintsEnabled: boolean;
  fields: NewPatientFormFieldInput[];
}

/** One option on a shared list (doctor, facility, …) with its rollover hint. */
export interface NewPatientFormOptionHint {
  id: number;
  name: string;
  active: boolean;
  hint: string;
  parentId: number | null;
}

export type NewPatientFormOptionHints = Record<OptionHintList, NewPatientFormOptionHint[]>;

export interface NewPatientFormOptionHintInput {
  list: OptionHintList;
  id: number;
  hint: string;
}

export function getNewPatientFormAdmin(): Promise<NewPatientFormAdminDocument> {
  return apiClient
    .get<ApiResponse<NewPatientFormAdminDocument>>('/admin/new-patient-form')
    .then((r) => r.data.data);
}

export function updateNewPatientFormAdmin(
  data: NewPatientFormAdminInput,
): Promise<NewPatientFormAdminDocument> {
  return apiClient
    .put<ApiResponse<NewPatientFormAdminDocument>>('/admin/new-patient-form', data)
    .then((r) => r.data.data);
}

export function getNewPatientFormOptionHints(): Promise<NewPatientFormOptionHints> {
  return apiClient
    .get<ApiResponse<NewPatientFormOptionHints>>('/admin/new-patient-form/option-hints')
    .then((r) => r.data.data);
}

/** Send only the hints that changed; errors come back keyed by array index. */
export function updateNewPatientFormOptionHints(
  hints: NewPatientFormOptionHintInput[],
): Promise<NewPatientFormOptionHints> {
  return apiClient
    .put<ApiResponse<NewPatientFormOptionHints>>('/admin/new-patient-form/option-hints', { hints })
    .then((r) => r.data.data);
}

// ─── Lead Fields ──────────────────────────────────────────────────────────────

/**
 * One `contacts_fields` row. `fieldOptions` is the "Field" of the legacy page:
 * the text matched against incoming lead emails and form question labels.
 */
export interface LeadField {
  contactFieldId: number;
  fieldTitle: string;
  fieldOptions: string;
  shortcode: string;
}

export type LeadFieldInput = Pick<LeadField, 'fieldTitle' | 'fieldOptions' | 'shortcode'>;

export function listLeadFields(): Promise<LeadField[]> {
  return apiClient
    .get<ApiResponse<LeadField[]>>('/admin/lead-fields')
    .then((r) => r.data.data);
}

export function createLeadField(data: LeadFieldInput): Promise<LeadField> {
  return apiClient
    .post<ApiResponse<LeadField>>('/admin/lead-fields', data)
    .then((r) => r.data.data);
}

export function updateLeadField(
  id: number,
  data: LeadFieldInput,
): Promise<LeadField> {
  return apiClient
    .put<ApiResponse<LeadField>>(`/admin/lead-fields/${id}`, data)
    .then((r) => r.data.data);
}

export function deleteLeadField(id: number): Promise<void> {
  return apiClient.delete(`/admin/lead-fields/${id}`).then(() => undefined);
}

// ─── Lead Sources ─────────────────────────────────────────────────────────────

export interface UrlParam {
  param: string;
  value: string;
}

export interface LeadSourceItem {
  referralLevel2ID: number;
  rootID: number;
  name: string;
  hint: string;
  emailDomain: string;
  image: string;
  imageThumb: string;
  urlParams: UrlParam[];
  active: string | boolean;
  rl2Order: number;
  accountID: number;
}

export interface LeadSourceCategory {
  referralLevel1ID: number;
  accountID: number;
  name: string;
  hint: string;
  active: string | boolean;
  rl1Order: number;
  /**
   * True for the category that means "referred by an existing patient", which is
   * collected with a patient picker rather than a lead source list. Resolved by the
   * API so the front-ends cannot disagree about which category it is.
   */
  isPatientReferralCategory?: boolean;
  leadSources: LeadSourceItem[];
}

export function listLeadSourceCategories(): Promise<LeadSourceCategory[]> {
  return apiClient
    .get<ApiResponse<LeadSourceCategory[]>>('/lead-source-categories')
    .then((r) => r.data.data ?? []);
}

export function createLeadSourceCategory(
  data: Pick<LeadSourceCategory, 'name'> & Partial<Pick<LeadSourceCategory, 'hint' | 'active'>>,
): Promise<LeadSourceCategory> {
  return apiClient
    .post<ApiResponse<LeadSourceCategory>>('/lead-source-categories', data)
    .then((r) => r.data.data);
}

export function updateLeadSourceCategory(
  id: number,
  data: Partial<Pick<LeadSourceCategory, 'name' | 'hint' | 'active'>>,
): Promise<LeadSourceCategory> {
  return apiClient
    .put<ApiResponse<LeadSourceCategory>>(`/lead-source-categories/${id}`, data)
    .then((r) => r.data.data);
}

export function deleteLeadSourceCategory(id: number): Promise<void> {
  return apiClient.delete(`/lead-source-categories/${id}`).then(() => undefined);
}

export function createLeadSourceItem(
  categoryId: number,
  data: Record<string, unknown>,
): Promise<LeadSourceItem> {
  return apiClient
    .post<ApiResponse<LeadSourceItem>>(
      `/lead-source-categories/${categoryId}/lead-sources`,
      data,
    )
    .then((r) => r.data.data);
}

export function updateLeadSourceItem(
  categoryId: number,
  sourceId: number,
  data: Record<string, unknown>,
): Promise<LeadSourceItem> {
  return apiClient
    .put<ApiResponse<LeadSourceItem>>(
      `/lead-source-categories/${categoryId}/lead-sources/${sourceId}`,
      data,
    )
    .then((r) => r.data.data);
}

export function deleteLeadSourceItem(
  categoryId: number,
  sourceId: number,
): Promise<void> {
  return apiClient
    .delete(`/lead-source-categories/${categoryId}/lead-sources/${sourceId}`)
    .then(() => undefined);
}

// ─── Plan Types ───────────────────────────────────────────────────────────────

export interface PlanType {
  planTypeId: number;
  accountId: number;
  name: string;
  active: boolean;
  pipelineTypeId: number;
}

export function listPlanTypes(): Promise<PlanType[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/admin/plan-types')
    .then((r) => (r.data.data ?? []).map((row) => normalizePlanType(row)));
}

// ─── Timeframes ───────────────────────────────────────────────────────────────

export interface Timeframe {
  timeframeId: number;
  name: string;
}

export function listTimeframes(): Promise<Timeframe[]> {
  return apiClient
    .get<ApiResponse<Timeframe[]>>('/admin/timeframes')
    .then((r) => r.data.data ?? []);
}

// ─── Promo Codes ──────────────────────────────────────────────────────────────

export interface PromoCode {
  promoCodeID: number;
  paymentDiscountID: number;
  promoCode: string;
  promoTitle: string;
  description: string;
  userID: number;
  type: string;
  applyTo: number;
  availableTo: number;
  startDate: string;
  stopDate: string;
  maxUses: number;
  patientUses: number;
  currentUses: number;
  status: number;
  endDate: string;
  createDate: string;
  updateDate: string;
}

export function listPromoCodes(): Promise<PromoCode[]> {
  return apiClient
    .get<ApiResponse<PromoCode[]>>('/promo-codes?include_inactive')
    .then((r) => r.data.data ?? []);
}

export function createPromoCode(
  data: Record<string, unknown>,
): Promise<PromoCode> {
  return apiClient
    .post<ApiResponse<PromoCode>>('/promo-codes', data)
    .then((r) => r.data.data);
}

export function updatePromoCode(
  id: number,
  data: Record<string, unknown>,
): Promise<PromoCode> {
  return apiClient
    .put<ApiResponse<PromoCode>>(`/promo-codes/${id}`, data)
    .then((r) => r.data.data);
}

export function deletePromoCode(id: number): Promise<void> {
  return apiClient.delete(`/promo-codes/${id}`).then(() => undefined);
}

export function restorePromoCode(id: number): Promise<void> {
  return apiClient.post(`/promo-codes/${id}/restore`).then(() => undefined);
}

export function listPromoCodeProducts(id: number): Promise<{ productTypeID: number }[]> {
  return apiClient
    .get<ApiResponse<{ productTypeID: number }[]>>(`/promo-codes/${id}/products`)
    .then((r) => r.data.data ?? []);
}

export function setPromoCodeProducts(id: number, productTypeIds: number[]): Promise<void> {
  return apiClient
    .post(`/promo-codes/${id}/products`, { productTypeIds })
    .then(() => undefined);
}

export function listPromoCodeProductCategories(id: number): Promise<{ productCategoryID: number }[]> {
  return apiClient
    .get<ApiResponse<{ productCategoryID: number }[]>>(`/promo-codes/${id}/product-categories`)
    .then((r) => r.data.data ?? []);
}

export function setPromoCodeProductCategories(id: number, productCategoryIds: number[]): Promise<void> {
  return apiClient
    .post(`/promo-codes/${id}/product-categories`, { productCategoryIds })
    .then(() => undefined);
}
